import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { JournalEntryListItem } from './JournalEntryListItem';
import { JournalEntryInlineFeed } from './JournalEntryInlineFeed';
import {
  useJournalEntries,
  useJournalLocksRealtime,
  useCreateJournalEntry,
  useArchiveJournalEntry,
  useRestoreJournalEntry,
  type JournalEntryWithAuthor,
} from '@/hooks/useJournal';
import { useJournalTemplates } from '@/hooks/useJournalTemplates';
import { JournalAttachments } from './JournalAttachments';
import {
  useAttachJournalEntryDocument,
  validateJournalAttachmentFile,
} from '@/hooks/useJournalAttachments';
import { useUploadDocument } from '@/hooks/useDocuments';

interface JournalSectionProps {
  clinicId: string;
  patientId: string;
  currentUserId: string | null | undefined;
  isAdmin: boolean;
}


export function JournalSection({
  clinicId,
  patientId,
  currentUserId,
  isAdmin,
}: JournalSectionProps) {
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const entriesQuery = useJournalEntries(clinicId, {
    patientId,
    includeArchived: showArchived,
  });
  const locksQuery = useJournalLocksRealtime(clinicId);
  const templatesQuery = useJournalTemplates(clinicId, patientId);
  const createEntry = useCreateJournalEntry(clinicId);
  const archiveEntry = useArchiveJournalEntry(clinicId);
  const restoreEntry = useRestoreJournalEntry(clinicId);
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachJournalEntryDocument(clinicId);

  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const templates = templatesQuery.data ?? [];
  const patientTemplates = templates.filter((t) => t.patient_id === patientId);
  const clinicTemplates = templates.filter((t) => t.patient_id === null);

  const resetCreateForm = () => {
    setNewTitle('');
    setNewContent('');
    setSelectedTemplateId('');
    setPendingFiles([]);
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setNewTitle('');
      setNewContent('');
      return;
    }
    const tmpl = templates.find((item) => item.id === templateId);
    if (tmpl) {
      setNewTitle(tmpl.title);
      setNewContent(tmpl.content_md);
    }
  };

  const handleCreate = async () => {
    let entry;
    try {
      entry = await createEntry.mutateAsync({
        patient_id: patientId,
        title: newTitle || undefined,
        content_md: newContent || undefined,
      });
    } catch {
      toast.error(t('common.error'));
      return;
    }

    for (const file of pendingFiles) {
      const validationError = validateJournalAttachmentFile(file);
      if (validationError) {
        toast.error(t(`journal.attachments.errors.${validationError}`));
        continue;
      }
      try {
        const doc = await uploadDocument.mutateAsync({
          clinicId,
          patientId,
          file,
          tags: ['journal'],
        });
        await attachDocument.mutateAsync({
          journal_entry_id: entry.id,
          document_id: doc.id,
        });
      } catch {
        toast.error(t('journal.attachments.errors.upload_failed'));
      }
    }

    toast.success(t('journal.entry_created'));
    setShowCreate(false);
    resetCreateForm();
  };

  const handleArchive = async (entry: JournalEntryWithAuthor) => {
    await archiveEntry.mutateAsync(entry.id);
    toast.success(t('journal.entry_archived'));
  };

  const handleRestore = async (entry: JournalEntryWithAuthor) => {
    await restoreEntry.mutateAsync(entry.id);
    toast.success(t('journal.entry_restored'));
  };

  const [dateFilter, setDateFilter] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [scrollToEntryId, setScrollToEntryId] = useState<string | null>(null);

  const entries = entriesQuery.data ?? [];
  const locks = locksQuery.data ?? [];

  return (
    <section className="flex h-[calc(100vh-280px)] gap-4 overflow-hidden">
      {/* Left Sidebar - Navigation */}
      <div className="w-80 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-card-dark">
        <div className="flex h-full flex-col">
          <div className="border-b border-gray-200 p-4 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('journal.section_title')}
            </h3>
            <div className="mt-3 space-y-2">
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="text-xs text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
              >
                {showArchived ? t('journal.hide_archived') : t('journal.show_archived')}
              </button>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="date"
                    className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    value={dateFilter.from}
                    onChange={(e) => setDateFilter((prev) => ({ ...prev, from: e.target.value }))}
                    placeholder={t('journal.filter_from')}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="date"
                    className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    value={dateFilter.to}
                    onChange={(e) => setDateFilter((prev) => ({ ...prev, to: e.target.value }))}
                    placeholder={t('journal.filter_to')}
                  />
                </div>
                {(dateFilter.from || dateFilter.to) && (
                  <button
                    type="button"
                    onClick={() => setDateFilter({ from: '', to: '' })}
                    className="text-xs text-gray-500 underline hover:text-indigo-600"
                  >
                    {t('common.clear_filters')}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {entriesQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : entries.length === 0 ? (
              <p className="py-6 text-center text-xs text-gray-400 dark:text-gray-500">
                {t('journal.no_entries')}
              </p>
            ) : (
              <div className="space-y-1">
                {entries.map((entry) => (
                  <JournalEntryListItem
                    key={entry.id}
                    entry={entry}
                    locks={locks}
                    currentUserId={currentUserId}
                    onClick={() => setScrollToEntryId(entry.id)}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Feed - Continuous View */}
      <div className="flex-1 overflow-y-auto">
        {showCreate && (
          <div className="mb-4 rounded-lg border border-indigo-200 bg-white p-4 shadow-sm dark:border-indigo-700 dark:bg-card-dark">
            <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              {t('journal.new_entry')}
            </h3>
            <div className="space-y-3">
              <input
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t('journal.title_placeholder')}
              />

              {templates.length > 0 && (
                <select
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  <option value="">{t('journal.no_template')}</option>
                  {patientTemplates.length > 0 && (
                    <optgroup label={t('journal.patient_templates', 'Patient templates')}>
                      {patientTemplates.map((tmpl) => (
                        <option key={tmpl.id} value={tmpl.id}>
                          {tmpl.title}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {clinicTemplates.length > 0 && (
                    <optgroup label={t('journal.clinic_templates', 'Clinic templates')}>
                      {clinicTemplates.map((tmpl) => (
                        <option key={tmpl.id} value={tmpl.id}>
                          {tmpl.title}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}

              <textarea
                className="block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={6}
                placeholder={t('journal.content_placeholder')}
              />

              <JournalAttachments
                clinicId={clinicId}
                patientId={patientId}
                pendingFiles={pendingFiles}
                onPendingFilesChange={setPendingFiles}
              />

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" onClick={() => {
                  setShowCreate(false);
                  resetCreateForm();
                }} size="sm">
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreate} disabled={createEntry.isPending} size="sm">
                  {createEntry.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t('journal.create')
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {!showCreate && (
          <div className="mb-4">
            <Button
              onClick={() => {
                resetCreateForm();
                setShowCreate(true);
              }}
              size="sm"
            >
              <Plus className="h-4 w-4" />
              {t('journal.new_entry')}
            </Button>
          </div>
        )}

        {entriesQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : entries.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-card-dark">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {t('journal.no_entries')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <JournalEntryInlineFeed
                key={entry.id}
                entry={entry}
                locks={locks}
                clinicId={clinicId}
                patientId={patientId}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                onArchive={() => handleArchive(entry)}
                onRestore={() => handleRestore(entry)}
                scrollToId={scrollToEntryId}
                onScrollComplete={() => setScrollToEntryId(null)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
